"use client";

import { useRef, useState } from "react";

/** Format a number with commas (e.g. 1500000 -> "1,500,000"). Returns "" for 0/falsy. */
function formatWithCommas(n: number): string {
  if (!n) return "";
  return n.toLocaleString("en-US");
}

/** Strip non-digit characters (except leading minus) and parse to number. */
function parseNumericInput(raw: string): number {
  const cleaned = raw.replace(/[^0-9-]/g, "");
  const parsed = parseInt(cleaned, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * Controlled numeric input that displays values with comma separators.
 * Shows commas while typing; stores the raw number via onChange.
 */
export function NumericInput({
  value,
  onChange,
  placeholder,
  className,
  prefix,
  min,
}: {
  value: number;
  onChange: (n: number) => void;
  placeholder?: string;
  className?: string;
  /** Optional prefix shown inside the input (e.g. "$") */
  prefix?: string;
  min?: number;
}) {
  const [displayValue, setDisplayValue] = useState(() =>
    formatWithCommas(value),
  );
  const inputRef = useRef<HTMLInputElement>(null);

  // Keep display in sync when value changes externally (e.g. initial data load)
  const lastExternalValue = useRef(value);
  if (value !== lastExternalValue.current) {
    lastExternalValue.current = value;
    // Only update display if user isn't actively editing
    if (
      typeof document !== "undefined" &&
      document.activeElement !== inputRef.current
    ) {
      setDisplayValue(formatWithCommas(value));
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Allow empty input
    if (raw === "" || raw === prefix) {
      setDisplayValue("");
      onChange(0);
      return;
    }

    const stripped = prefix ? raw.replace(prefix, "") : raw;
    const num = parseNumericInput(stripped);
    if (min !== undefined && num < min) return;

    // Reformat with commas, preserving cursor position
    const el = inputRef.current;
    const cursorPos = el?.selectionStart ?? 0;
    const oldLen = raw.length;

    const formatted = formatWithCommas(num);
    setDisplayValue(formatted);
    onChange(num);

    // Restore cursor adjusted for comma changes
    requestAnimationFrame(() => {
      if (el) {
        const newLen = (prefix ? prefix : "").length + formatted.length;
        const diff = newLen - oldLen;
        const newPos = Math.max(0, cursorPos + diff);
        el.setSelectionRange(newPos, newPos);
      }
    });
  };

  const handleBlur = () => {
    // Clean up display on blur
    setDisplayValue(formatWithCommas(value));
  };

  const display =
    prefix && displayValue ? `${prefix}${displayValue}` : displayValue;
  const placeholderText =
    prefix && placeholder ? `${prefix}${placeholder}` : placeholder;

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      value={display}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholderText}
      className={className}
    />
  );
}
